/**
 * useWebSocket Hook - Real-time connection manager
 * Handles WebSocket connection, reconnection, and message handling
 */
import { useState, useEffect, useRef, useCallback } from 'react';

const RECONNECT_INTERVALS = [1000, 2000, 5000, 10000, 30000]; // Progressive backoff

export const useWebSocket = (options = {}) => {
  const {
    userId = 'anonymous',
    onRedAlert = null,
    onContactCreated = null,
    onContactUpdated = null,
    onContactStageChanged = null,
    onContactStatusChanged = null,
    onContactDeleted = null,
    onStatsUpdated = null,
    autoReconnect = true,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [lastMessage, setLastMessage] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const mountedRef = useRef(true);

  // Get WebSocket URL from environment
  const getWebSocketUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Convert http(s) to ws(s)
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const baseUrl = backendUrl.replace(/^https?/, wsProtocol);
    return `${baseUrl}/api/ws?user_id=${encodeURIComponent(userId)}`;
  }, [userId]);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  // Handle incoming messages
  const handleMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      setLastMessage(data);

      switch (data.type) {
        case 'connected':
          console.log('[WS] Connected to server', data.payload);
          break;

        case 'red_alert':
          console.log('[WS] RED ALERT received', data.payload);
          if (onRedAlert) onRedAlert(data.payload);
          break;

        case 'contact_created':
          console.log('[WS] Contact created', data.payload?.contact?.contact_id);
          if (onContactCreated) onContactCreated(data.payload);
          break;

        case 'contact_updated':
          console.log('[WS] Contact updated', data.payload?.contact_id);
          if (onContactUpdated) onContactUpdated(data.payload);
          break;

        case 'contact_stage_changed':
          console.log('[WS] Stage changed', data.payload?.contact_id, data.payload?.new_stage);
          if (onContactStageChanged) onContactStageChanged(data.payload);
          break;

        case 'contact_status_changed':
          console.log('[WS] Status changed', data.payload?.contact_id, data.payload?.new_status);
          if (onContactStatusChanged) onContactStatusChanged(data.payload);
          break;

        case 'contact_deleted':
          console.log('[WS] Contact deleted', data.payload?.contact_id);
          if (onContactDeleted) onContactDeleted(data.payload);
          break;

        case 'stats_updated':
          console.log('[WS] Stats updated');
          if (onStatsUpdated) onStatsUpdated(data.payload);
          break;

        case 'pong':
          // Keep-alive response - no action needed
          break;

        default:
          console.log('[WS] Unknown message type:', data.type);
      }
    } catch (error) {
      console.error('[WS] Failed to parse message:', error);
    }
  }, [onRedAlert, onContactCreated, onContactUpdated, onContactStageChanged, onContactStatusChanged, onContactDeleted, onStatsUpdated]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    
    clearTimers();
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    try {
      const url = getWebSocketUrl();
      console.log('[WS] Connecting to:', url);
      
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        
        console.log('[WS] Connection opened');
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttemptRef.current = 0;

        // Start ping interval (every 30 seconds)
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      ws.onmessage = handleMessage;

      ws.onclose = (event) => {
        if (!mountedRef.current) return;
        
        console.log('[WS] Connection closed:', event.code, event.reason);
        setIsConnected(false);
        clearTimers();

        // Attempt reconnect if enabled and not a clean close
        if (autoReconnect && event.code !== 1000) {
          const attempt = reconnectAttemptRef.current;
          const delay = RECONNECT_INTERVALS[Math.min(attempt, RECONNECT_INTERVALS.length - 1)];
          
          console.log(`[WS] Reconnecting in ${delay}ms (attempt ${attempt + 1})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptRef.current++;
            connect();
          }, delay);
        }
      };

      ws.onerror = (error) => {
        if (!mountedRef.current) return;
        
        console.error('[WS] Connection error:', error);
        setConnectionError('Connection failed');
      };

    } catch (error) {
      console.error('[WS] Failed to create WebSocket:', error);
      setConnectionError(error.message);
    }
  }, [getWebSocketUrl, handleMessage, autoReconnect, clearTimers]);

  // Disconnect
  const disconnect = useCallback(() => {
    clearTimers();
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, [clearTimers]);

  // Send message
  const sendMessage = useCallback((type, payload = {}) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, payload }));
      return true;
    }
    return false;
  }, []);

  // Connect on mount
  useEffect(() => {
    mountedRef.current = true;
    connect();

    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, [connect, disconnect]);

  // Reconnect when userId changes
  useEffect(() => {
    if (isConnected) {
      disconnect();
      connect();
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isConnected,
    connectionError,
    lastMessage,
    connect,
    disconnect,
    sendMessage,
  };
};

export default useWebSocket;
